import { useEffect, useMemo, useState } from 'react'
import { listerClients, listerLivreurs, listerMarchands } from '../../api/dispatch'
import type { Client, Commande, Livreur, Marchand } from '../../api/types'
import { fcfa } from '../Format'
import { Panneau } from '../Modale'
import type { Casquette } from '../../api/discussions'

/**
 * Contacts — TRANSPOSITION du gabarit d'origine.
 *
 * Le portage avait remplacé les PUCES par des sous-onglets, la recherche par
 * un champ texte, et les lignes de contact par un tableau. Résultat : aucune
 * des quatre classes de l'annuaire (`.contact-row`, `.ci-av`, `.ci-b`,
 * `.ci-ar`) n'était posée, et surtout aucune ligne n'était CLIQUABLE — alors
 * que c'est tout l'intérêt de l'écran : ouvrir la fiche d'un client qui
 * appelle.
 */
type Famille = 'clients' | 'marchands' | 'livreurs'

const ETIQUETTE_LIVREUR: Record<string, [string, string]> = {
  online: ['En ligne', 'green'],
  available: ['En ligne', 'green'],
  busy: ['En course', 'yellow'],
  delivering: ['En course', 'yellow'],
  offline: ['Hors ligne', 'gray'],
}

const Chevron = () => (
  <svg className="ci-ar" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="9 18 15 12 9 6" />
  </svg>
)

function LigneContact({
  initiale,
  nom,
  sous,
  surClic,
}: {
  initiale: string
  nom: string
  sous: React.ReactNode
  surClic: () => void
}) {
  return (
    <div className="contact-row" onClick={surClic}>
      <div className="ci-av">{initiale}</div>
      <div className="ci-b">
        <div className="ci-n">{nom}</div>
        <div className="ci-s">{sous}</div>
      </div>
      <Chevron />
    </div>
  )
}

/** Client enrichi de ses agrégats de commandes. */
interface ClientAnnuaire {
  id: string | null
  nom: string
  telephone: string
  email: string
  commandes: number
  total: number
  derniere: string | null
  inscrit: string | null
}

export function EcranContacts({
  commandes,
  surContacter,
  surMarchand,
  surLivreur,
}: {
  commandes: Commande[]
  surContacter: (pairId: string, casquette: Casquette) => void
  surMarchand: (m: Marchand) => void
  surLivreur: (l: Livreur) => void
}) {
  const [famille, setFamille] = useState<Famille>('clients')
  const [clients, setClients] = useState<Client[]>([])
  const [livreurs, setLivreurs] = useState<Livreur[]>([])
  const [marchands, setMarchands] = useState<Marchand[]>([])
  const [erreur, setErreur] = useState<string | null>(null)
  const [fiche, setFiche] = useState<ClientAnnuaire | null>(null)

  useEffect(() => {
    let vivant = true
    void Promise.all([listerClients(), listerLivreurs(), listerMarchands()])
      .then(([c, l, m]) => {
        if (!vivant) return
        setClients(c)
        setLivreurs(l)
        setMarchands(m)
      })
      .catch((e: unknown) =>
        vivant && setErreur(e instanceof Error ? e.message : 'Lecture impossible.'),
      )
    return () => {
      vivant = false
    }
  }, [])

  /**
   * L'annuaire des clients est CONSTRUIT à partir des commandes, pas seulement
   * de la table `profiles` : beaucoup de commandes sont passées sans compte.
   * Les ignorer ferait disparaître de l'annuaire des gens qui appellent.
   */
  const annuaireClients = useMemo<ClientAnnuaire[]>(() => {
    const par = new Map<string, ClientAnnuaire>()
    for (const c of clients) {
      par.set(c.id, {
        id: c.id,
        nom: c.nom,
        telephone: c.telephone,
        email: c.email,
        commandes: 0,
        total: 0,
        derniere: null,
        inscrit: c.creeLe,
      })
    }
    for (const o of commandes) {
      if (!o.clientId) continue
      let e = par.get(o.clientId)
      if (!e) {
        e = {
          id: o.clientId,
          nom: o.clientNom || 'Client',
          telephone: o.clientTelephone,
          email: '',
          commandes: 0,
          total: 0,
          derniere: null,
          inscrit: null,
        }
        par.set(o.clientId, e)
      }
      e.commandes++
      e.total += o.total
      if (!e.derniere || new Date(o.creeeLe) > new Date(e.derniere)) e.derniere = o.creeeLe
    }
    // Les plus actifs d'abord : c'est eux qu'on cherche le plus souvent.
    return [...par.values()].sort((a, b) => b.commandes - a.commandes || a.nom.localeCompare(b.nom))
  }, [clients, commandes])

  const ONGLETS: Array<[Famille, string, number]> = [
    ['clients', 'Clients', annuaireClients.length],
    ['marchands', 'Marchands', marchands.length],
    ['livreurs', 'Livreurs', livreurs.length],
  ]

  const initiale = (s: string) => (s || '?').charAt(0)

  return (
    <>
      <div className="chip-row">
        {ONGLETS.map(([cle, libelle, n]) => (
          <button
            key={cle}
            className={`chip${famille === cle ? ' active' : ''}`}
            onClick={() => setFamille(cle)}
          >
            {libelle} <span className="chip-n">{n}</span>
          </button>
        ))}
      </div>

      {erreur && <p style={{ color: 'var(--danger)', fontSize: 13 }}>{erreur}</p>}

      <div className="card">
        <div className="card-body" style={{ padding: '4px 0' }}>
          {famille === 'clients' &&
            annuaireClients.map((c, i) => (
              <LigneContact
                key={c.id ?? i}
                initiale={initiale(c.nom)}
                nom={c.nom || 'Client'}
                sous={`${c.telephone || c.email || '—'} · ${c.commandes ? `${c.commandes} commande${c.commandes > 1 ? 's' : ''}` : 'aucune commande'}`}
                surClic={() => setFiche(c)}
              />
            ))}

          {famille === 'marchands' &&
            marchands.map((m) => (
              <LigneContact
                key={m.id}
                initiale={initiale(m.nom)}
                nom={m.nom}
                sous={`${m.type} · ${m.quartier} · ${m.telephone}`}
                surClic={() => surMarchand(m)}
              />
            ))}

          {famille === 'livreurs' &&
            livreurs.map((l) => {
              const [libelle, couleur] = ETIQUETTE_LIVREUR[l.etat] ?? ['Hors ligne', 'gray']
              return (
                <LigneContact
                  key={l.id}
                  initiale={initiale(l.nom)}
                  nom={l.nom}
                  sous={
                    <>
                      {l.telephone} ·{' '}
                      <span style={{ color: `var(--${couleur === 'gray' ? 'n400' : couleur})` }}>
                        {libelle}
                      </span>
                    </>
                  }
                  surClic={() => surLivreur(l)}
                />
              )
            })}

          {((famille === 'clients' && annuaireClients.length === 0) ||
            (famille === 'marchands' && marchands.length === 0) ||
            (famille === 'livreurs' && livreurs.length === 0)) && (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--n400)' }}>
              Aucun contact.
            </div>
          )}
        </div>
      </div>

      <Panneau
        titre={fiche?.nom || 'Client'}
        ouvert={Boolean(fiche)}
        surFermer={() => setFiche(null)}
      >
        {fiche && (
          <>
            <div
              style={{
                fontSize: 11,
                color: 'var(--n400)',
                textTransform: 'uppercase',
                letterSpacing: '.5px',
                marginBottom: 6,
              }}
            >
              Client
            </div>
            {fiche.id ? (
              <button
                className="btn btn-accent btn-sm"
                style={{ marginBottom: 10 }}
                onClick={() => {
                  const id = fiche.id!
                  setFiche(null)
                  surContacter(id, 'client')
                }}
              >
                Contacter (chat)
              </button>
            ) : (
              /* Sans compte, il n'y a personne à qui écrire. Le dire évite de
                 chercher un bouton qui ne peut pas exister. */
              <div style={{ fontSize: 11.5, color: 'var(--n400)', marginBottom: 10 }}>
                Chat indisponible : commandes passées sans compte client.
              </div>
            )}
            <FicheLigne libelle="E-mail" valeur={fiche.email} />
            <FicheLigne libelle="Telephone" valeur={fiche.telephone} />
            <FicheLigne libelle="Commandes" valeur={String(fiche.commandes)} />
            <FicheLigne libelle="Total depense" valeur={fcfa(fiche.total)} />
            <FicheLigne
              libelle="Derniere commande"
              valeur={fiche.derniere ? new Date(fiche.derniere).toLocaleDateString('fr-ML') : 'aucune'}
            />
            {fiche.inscrit && (
              <FicheLigne
                libelle="Inscrit le"
                valeur={new Date(fiche.inscrit).toLocaleDateString('fr-ML')}
              />
            )}
          </>
        )}
      </Panneau>
    </>
  )
}

const FicheLigne = ({ libelle, valeur }: { libelle: string; valeur: string }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      padding: '9px 0',
      borderBottom: '1px solid var(--n200)',
      fontSize: 13,
    }}
  >
    <span style={{ color: 'var(--n400)' }}>{libelle}</span>
    <span style={{ fontWeight: 600 }}>{valeur || '—'}</span>
  </div>
)
